import { useMemo, useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Checkbox } from './ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { mdiArrowLeft, mdiContentSave, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import {
  useComplianceControl, useComplianceControls, useUpdateComplianceControl, useDeleteComplianceControl,
  COMPLIANCE_DOMAINS
} from '../hooks/useComplianceApi'
import { useDebounce } from '../hooks/useDebounce'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useToast } from '../hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { useCan } from '../contexts/PermissionsContext'

export function ComplianceControlDetailPage() {
  const { org, controlUid } = useParams({ strict: false })
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation('compliance')
  // Editing a compliance control is `compliance.write`. Without it the form
  // stays on screen (it is reachable as read navigation from the controls
  // list) but renders read-only, and the Save/Delete actions disappear
  // (spec 2026-09-04-02, "Gating").
  const canWrite = useCan('compliance.write')

  const { data: control, isLoading, error } = useComplianceControl(org || 'demo', controlUid || '')
  const { data: allControls = [] } = useComplianceControls(org || 'demo')
  const updateMutation = useUpdateComplianceControl(org || 'demo', controlUid || '')
  const deleteMutation = useDeleteComplianceControl(org || 'demo')

  const [code, setCode] = useState('')
  const [domain, setDomain] = useState<string>('')
  const [nameEn, setNameEn] = useState('')
  const [nameFr, setNameFr] = useState('')
  const [descriptionEn, setDescriptionEn] = useState('')
  const [descriptionFr, setDescriptionFr] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [position, setPosition] = useState(0)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const debouncedCode = useDebounce(code, 300)
  const codeDuplicate = useMemo(() => {
    if (!debouncedCode.trim()) return false
    return allControls.some(c => c.code === debouncedCode.trim() && c.uid !== controlUid)
  }, [debouncedCode, allControls, controlUid])

  useEffect(() => {
    if (control) {
      setCode(control.code || '')
      setDomain(control.domain || '')
      setNameEn(control.name?.en || '')
      setNameFr(control.name?.fr || '')
      setDescriptionEn(control.description?.en || '')
      setDescriptionFr(control.description?.fr || '')
      setEnabled(control.enabled)
      setPosition(control.position || 0)
    }
  }, [control])

  const breadcrumbs = useMemo(() => [
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.org_settings'), path: `/${org}/settings` },
    { title: t('breadcrumb.compliance'), path: `/${org}/compliance-controls` },
    { title: control ? (control.name?.en || control.code) : t('breadcrumb.edit') }
  ], [org, control, t])
  useBreadcrumbs(breadcrumbs)

  const handleSave = async () => {
    if (!control) return

    const name: Record<string, string> = {}
    if (nameEn) name.en = nameEn
    if (nameFr) name.fr = nameFr

    const description: Record<string, string> = {}
    if (descriptionEn) description.en = descriptionEn
    if (descriptionFr) description.fr = descriptionFr

    try {
      await updateMutation.mutateAsync({
        code: code.trim() || undefined,
        domain: domain || undefined,
        name: Object.keys(name).length > 0 ? name : undefined,
        description: Object.keys(description).length > 0 ? description : undefined,
        enabled,
        position,
      } as any)
      navigate({ to: '/$org/compliance-controls', params: { org: org || 'demo' }, search: { domain_code: undefined } })
    } catch (error) {
      toast({
        title: t('common:status.error'),
        description: (error as any)?.title || t('controls.error_save'),
        variant: 'destructive'
      })
    }
  }

  const handleDelete = async () => {
    if (!control) return
    try {
      await deleteMutation.mutateAsync(control.uid)
      navigate({ to: '/$org/compliance-controls', params: { org: org || 'demo' }, search: { domain_code: undefined } })
    } catch (error) {
      toast({
        title: t('common:status.error'),
        description: (error as any)?.title || t('controls.error_delete'),
        variant: 'destructive'
      })
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  if (error || !control) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">Error</h1>
          <p className="text-muted-foreground">
            {(error as any)?.title || 'Control not found'}
          </p>
          <Link to="/$org/compliance-controls" params={{ org: org || 'demo' }} search={{ domain_code: undefined }}>
            <Button className="mt-4"><AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />{t('controls.back_to_controls')}</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t('controls.edit_title')}</h1>
            </div>
          </div>
          {canWrite && (
            <div className="flex items-center space-x-2">
              <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)} disabled={deleteMutation.isPending} data-testid="delete-control-btn">
                <AppIcon path={mdiTrashCanOutline} size="sm" className="mr-2" />{t('common:actions.delete')}
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending || codeDuplicate} data-testid="save-control-btn">
                <AppIcon path={mdiContentSave} size="sm" className="mr-2" />{t('common:actions.save')}
              </Button>
            </div>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('controls.form_title')}</CardTitle>
            <CardDescription>{t('controls.form_description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">{t('controls.field_code')}</Label>
                <Input id="code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))} readOnly={!canWrite} data-testid="code-input" />
                {codeDuplicate && (
                  <p className="text-xs text-destructive" data-testid="code-duplicate-error">
                    {t('controls.code_already_exists')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="domain">{t('controls.field_domain')}</Label>
                <select
                  id="domain"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  disabled={!canWrite}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs"
                  data-testid="domain-select"
                >
                  {COMPLIANCE_DOMAINS.map(d => (
                    <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name-en">{t('controls.field_name_en')}</Label>
              <Input id="name-en" value={nameEn} onChange={(e) => setNameEn(e.target.value)} readOnly={!canWrite} data-testid="name-en-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name-fr">{t('controls.field_name_fr')}</Label>
              <Input id="name-fr" value={nameFr} onChange={(e) => setNameFr(e.target.value)} readOnly={!canWrite} data-testid="name-fr-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description-en">{t('controls.field_description_en')}</Label>
              <Textarea id="description-en" value={descriptionEn} onChange={(e) => setDescriptionEn(e.target.value)} rows={3} readOnly={!canWrite} data-testid="description-en-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description-fr">{t('controls.field_description_fr')}</Label>
              <Textarea id="description-fr" value={descriptionFr} onChange={(e) => setDescriptionFr(e.target.value)} rows={3} readOnly={!canWrite} data-testid="description-fr-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox id="enabled" checked={enabled} onCheckedChange={setEnabled} disabled={!canWrite} data-testid="enabled-switch" />
                <Label htmlFor="enabled">{t('controls.field_enabled')}</Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">{t('controls.field_position')}</Label>
                <Input id="position" type="number" value={position} onChange={(e) => setPosition(parseInt(e.target.value) || 0)} readOnly={!canWrite} data-testid="position-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <Label className="text-sm text-muted-foreground">{t('controls.field_created')}</Label>
                <p className="text-sm">{control.created_at ? new Date(control.created_at).toLocaleString() : '-'}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('controls.field_updated')}</Label>
                <p className="text-sm">{control.updated_at ? new Date(control.updated_at).toLocaleString() : '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('controls.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('controls.delete_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-btn">{t('common:actions.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
